'use client';

import type React from 'react';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { cn } from '@/utils/utils';

export interface ChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  onToggle?: () => void;
}

export function Chip({
  children,
  className,
  selected,
  onToggle,
  ...props
}: ChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'px-4 py-2 rounded-full text-sm font-medium transition-colors border',
        selected
          ? 'bg-secondary text-black border-yellow-300'
          : 'text-gray-700 border-gray-200',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export interface ChipsGroupProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  options: Array<{ value: string; label: string }>;
  className?: string;
  multiSelect?: boolean;
}

export function ChipsGroup<T extends FieldValues>({
  name,
  control,
  options,
  className,
  multiSelect = true,
}: ChipsGroupProps<T>) {
  return (
    <div className={className}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className="flex flex-wrap gap-3">
            {options.map((option) => {
              const isSelected = multiSelect
                ? Array.isArray(field.value) && field.value.includes(option.value)
                : field.value === option.value;

              const handleToggle = () => {
                if (multiSelect) {
                  const currentValues = Array.isArray(field.value) ? field.value : [];

                  if (isSelected) {
                    field.onChange(
                      currentValues.filter((value) => value !== option.value),
                    );
                  } else {
                    field.onChange([...currentValues, option.value]);
                  }
                } else {
                  // Single select mode
                  field.onChange(option.value);
                }
              };

              return (
                <Chip
                  key={option.value}
                  selected={isSelected}
                  onToggle={handleToggle}
                >
                  {option.label}
                </Chip>
              );
            })}
          </div>
        )}
      />
    </div>
  );
}
